import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Badge, Container } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { apiClient } from '../utils/apiUtils';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    myProjects: 0,
    projectsByStatus: null,
    totalProjects: 0,
    totalPrograms: 0,
    totalActivities: 0,
    initiativesByType: null
  });
  const [elevatedStats, setElevatedStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user has elevated role
  const elevatedRoles = ['Extension Head', 'GAD', 'Vice Chancellor for Administration and Finance', 'Vice Chancellor for Research, Development and Extension Services', 'Chancellor', 'Admin'];
  const isElevatedRole = elevatedRoles.includes(user?.role);
  const isExtensionCoordinator = user?.role === 'Extension Coordinator';
  
  console.log('Dashboard Debug Info:');
  console.log('- User object:', user);
  console.log('- User role:', user?.role);
  console.log('- Elevated roles list:', elevatedRoles);
  console.log('- Is elevated role:', isElevatedRole);
  console.log('- Loading state:', loading);

  useEffect(() => {
    console.log('Dashboard useEffect - user:', user ? 'present' : 'missing');
    console.log('Dashboard useEffect - token in localStorage:', localStorage.getItem('token') ? 'present' : 'missing');
    if (user && localStorage.getItem('token')) {
      console.log('Dashboard: User and token present, fetching stats...');
      fetchStats();
    } else {
      console.log('Dashboard: User or token missing, not fetching stats');
    }
  }, [user]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      console.log('fetchStats called - User role:', user?.role);
      console.log('fetchStats called - Is elevated role:', isElevatedRole);
      console.log('fetchStats called - Token in localStorage:', localStorage.getItem('token') ? 'present' : 'missing');
      console.log('fetchStats called - apiClient headers:', apiClient.defaults.headers.common);
      
      if (isElevatedRole) {
        // Fetch enhanced dashboard data for elevated roles
        console.log('Fetching elevated dashboard data...');
        const response = await apiClient.get('/projects/stats/elevated-dashboard');
        console.log('Elevated dashboard response:', response.data);
        setElevatedStats(response.data);
      } else {
        // Fetch regular dashboard data for other roles
      const response = await apiClient.get('/projects/stats/dashboard');
      const data = response.data;
        
        // For Extension Coordinators, also fetch recent activity
        let recentActivity = [];
        if (isExtensionCoordinator) {
          try {
            const activityResponse = await apiClient.get('/logs', {
              params: {
                limit: 50, // Get more to filter
                page: 1
              }
            });
            
            console.log('Recent Activity Debug:');
            console.log('- Raw logs:', activityResponse.data.logs);
            console.log('- Logs count:', activityResponse.data.logs?.length || 0);
            
            // Filter out system activities and focus on user inputs
            const filteredActivity = (activityResponse.data.logs || [])
              .filter(activity => {
                const isValidAction = activity.action && 
                  !['LOGIN', 'LOGOUT'].includes(activity.action) &&
                  ['CREATE', 'UPDATE', 'SUBMIT', 'UPLOAD', 'APPROVE', 'REJECT'].includes(activity.action);
                
                const isValidEntity = activity.entity_type && 
                  ['project', 'work_plan', 'budget', 'report', 'monitoring', 'projects', 'workplans', 'budgets', 'reports', 'monitoring_tools'].includes(activity.entity_type.toLowerCase());
                
                console.log(`Activity: ${activity.action} - ${activity.entity_type} - Valid: ${isValidAction && isValidEntity}`);
                return isValidAction && isValidEntity;
              })
              .slice(0, 5); // Take only the 5 most recent relevant activities
            
            console.log('- Filtered activities:', filteredActivity);
            recentActivity = filteredActivity;
          } catch (activityError) {
            console.error('Error fetching recent activity:', activityError);
          }
        }
        
        console.log('Dashboard data for Extension Coordinator:');
        console.log('- Total projects:', data.totalProjects);
        console.log('- Projects by status:', data.projectsByStatus);
        console.log('- Total programs:', data.totalPrograms);
        console.log('- Total activities:', data.totalActivities);
        console.log('- Approved by Chancellor count:', data.projectsByStatus?.['Approved by Chancellor']);
        
      setStats({
        myProjects: data.totalProjects || 0,
        projectsByStatus: data.projectsByStatus || null,
        totalProjects: data.totalProjects || 0,
        totalPrograms: data.totalPrograms || 0,
        totalActivities: data.totalActivities || 0,
          initiativesByType: data.initiativesByType || null,
          recentActivity: recentActivity
      });
      }
    } catch (error) {
      console.error('Error fetching project stats:', error);
      console.error('Error details:', error.response?.data || error.message);
      console.error('Error status:', error.response?.status);
      console.error('Error headers:', error.response?.headers);
      
      setError({
        message: error.response?.data?.message || error.message,
        status: error.response?.status,
        details: error.response?.data
      });
      
      // Set default values to prevent undefined errors
      if (isElevatedRole) {
        setElevatedStats({
          recentProjectsByDepartment: {},
          totalProjectsByDepartment: [],
          overallStats: {
            total_projects: 0,
            approved_events: 0,
            total_programs: 0,
            total_projects_count: 0,
            total_activities: 0
          }
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusVariant = (status) => {
    const statusMap = {
      'Pending': 'warning',
      'Approved by Chancellor': 'success',
      'On-Going': 'info',
      'Completed': 'primary',
      'Rejected': 'danger'
    };
    return statusMap[status] || 'secondary';
  };

  const getActivityIcon = (action) => {
    const iconMap = {
      'CREATE': 'bi-plus-circle',
      'UPDATE': 'bi-pencil-square',
      'DELETE': 'bi-trash',
      'LOGIN': 'bi-box-arrow-in-right',
      'LOGOUT': 'bi-box-arrow-right',
      'SUBMIT': 'bi-send',
      'APPROVE': 'bi-check-circle',
      'REJECT': 'bi-x-circle',
      'UPLOAD': 'bi-cloud-upload',
      'DOWNLOAD': 'bi-cloud-download'
    };
    return iconMap[action] || 'bi-activity';
  };

  const getActivityColor = (action) => {
    const colorMap = {
      'CREATE': 'success',
      'UPDATE': 'info',
      'DELETE': 'danger',
      'LOGIN': 'success',
      'LOGOUT': 'secondary',
      'SUBMIT': 'primary',
      'APPROVE': 'success',
      'REJECT': 'danger',
      'UPLOAD': 'info',
      'DOWNLOAD': 'primary'
    };
    return colorMap[action] || 'secondary';
  };

  const getStatusBadgeColor = (statusCode) => {
    const statusMap = {
      200: 'success',
      201: 'success',
      400: 'warning',
      401: 'danger',
      403: 'danger',
      404: 'warning',
      500: 'danger'
    };
    return statusMap[statusCode] || 'secondary';
  };

  const getActivityDescription = (activity) => {
    // If there's already a good description, use it
    if (activity.description && activity.description.length > 10) {
      return activity.description;
    }
    
    // Otherwise, create a meaningful description based on action and entity
    const actionMap = {
      'CREATE': 'Created',
      'UPDATE': 'Updated',
      'DELETE': 'Deleted',
      'SUBMIT': 'Submitted',
      'APPROVE': 'Approved',
      'REJECT': 'Rejected',
      'UPLOAD': 'Uploaded',
      'DOWNLOAD': 'Downloaded'
    };
    
    const entityMap = {
      'project': 'project proposal',
      'work_plan': 'work plan',
      'budget': 'budget proposal',
      'report': 'project report',
      'monitoring': 'monitoring data'
    };
    
    const action = actionMap[activity.action] || activity.action;
    const entity = entityMap[activity.entity_type?.toLowerCase()] || activity.entity_type;
    
    return `${action} ${entity}${activity.entity_id ? ` #${activity.entity_id}` : ''}`;
  };

  if (loading) {
    return (
      <div className="container-fluid px-3 px-md-4 py-3">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-3 px-md-4 py-3">
      {/* Welcome header */}
      <div className="mb-4">
        <h1 className="h3 mb-1">Welcome back, {user?.fullname || 'Administrator'}</h1>
        <p className="text-muted mb-0">
          {isElevatedRole 
            ? '' 
            : 'A quick glance at your extension initiatives.'
          }
        </p>
      </div>

      {isElevatedRole && elevatedStats && !loading ? (
        <>
          {/* Enhanced Dashboard for Elevated Roles */}
          
          {/* Overall Statistics Cards */}
          <Row className="g-3 mb-4">
            <Col md={3}>
              <Card className="h-100 shadow-sm border-0">
                <Card.Body className="d-flex align-items-center justify-content-between">
                  <div>
                    <div className="text-muted small">Total Programs</div>
                    <div className="fs-2 fw-semibold">{elevatedStats.overallStats?.total_programs || 0}</div>
                  </div>
                  <i className="bi bi-diagram-3-fill fs-1 text-primary"></i>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="h-100 shadow-sm border-0">
                <Card.Body className="d-flex align-items-center justify-content-between">
                  <div>
                    <div className="text-muted small">Total Projects</div>
                    <div className="fs-2 fw-semibold">{elevatedStats.overallStats?.total_projects_count || 0}</div>
                  </div>
                  <i className="bi bi-folder-fill fs-1 text-danger"></i>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="h-100 shadow-sm border-0">
                <Card.Body className="d-flex align-items-center justify-content-between">
                  <div>
                    <div className="text-muted small">Total Activities</div>
                    <div className="fs-2 fw-semibold">{elevatedStats.overallStats?.total_activities || 0}</div>
                  </div>
                  <i className="bi bi-star-fill fs-1 text-warning"></i>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="h-100 shadow-sm border-0">
                <Card.Body className="d-flex align-items-center justify-content-between">
                  <div>
                    <div className="text-muted small">Approved Events</div>
                    <div className="fs-2 fw-semibold">{elevatedStats.overallStats?.approved_events || 0}</div>
                  </div>
                  <i className="bi bi-check-circle-fill fs-1 text-success"></i>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Combined Charts Section */}
          <Row className="mb-4">
            <Col>
              <Card className="shadow-sm">
                <Card.Header>
                  <h5 className="mb-0">Projects Distribution by Department/College</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    {/* Approved Projects Pie Chart */}
                    <Col md={6}>
                      <h6 className="text-center mb-3">Approved Projects Distribution</h6>
                      {Object.keys(elevatedStats.recentProjectsByDepartment).length > 0 ? (
                        <div className="d-flex justify-content-center">
                          <div style={{ width: '400px', height: '350px' }}>
                            <Pie
                              data={{
                                labels: Object.keys(elevatedStats.recentProjectsByDepartment),
                                datasets: [
                                  {
                                    data: Object.values(elevatedStats.recentProjectsByDepartment).map(projects => projects.length),
                                    backgroundColor: [
                                      '#FF6384',
                                      '#36A2EB',
                                      '#FFCE56',
                                      '#4BC0C0',
                                      '#9966FF',
                                      '#FF9F40',
                                      '#FF6384',
                                      '#C9CBCF',
                                      '#4BC0C0',
                                      '#FF6384'
                                    ],
                                    borderWidth: 2,
                                    borderColor: '#fff'
                                  }
                                ]
                              }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                  legend: {
                                    position: 'bottom',
                                    labels: {
                                      usePointStyle: true,
                                      padding: 10,
                                      font: { size: 10 }
                                    }
                                  },
                                  tooltip: {
                                    callbacks: {
                                      label: function(context) {
                                        const label = context.label || '';
                                        const value = context.parsed;
                                        return `${label}: ${value} approved projects`;
                                      }
                                    }
                                  }
                                }
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="text-center text-muted py-3">
                          No approved projects found
                        </div>
                      )}
                    </Col>
                    
                    {/* Total Projects Bar Chart */}
                    <Col md={6}>
                      <h6 className="text-center mb-3">Total Projects by Department</h6>
                      {elevatedStats.totalProjectsByDepartment.length > 0 ? (
                        <div className="d-flex justify-content-center">
                          <div style={{ width: '400px', height: '350px' }}>
                            <Bar
                              data={{
                                labels: elevatedStats.totalProjectsByDepartment.map(dept => 
                                  dept.department_college.length > 15 
                                    ? dept.department_college.substring(0, 15) + '...' 
                                    : dept.department_college
                                ),
                                datasets: [
                                  {
                                    label: 'Total Projects',
                                    data: elevatedStats.totalProjectsByDepartment.map(dept => dept.total_projects),
                                    backgroundColor: '#36A2EB',
                                    borderColor: '#36A2EB',
                                    borderWidth: 1
                                  },
                                  {
                                    label: 'Approved by Chancellor',
                                    data: elevatedStats.totalProjectsByDepartment.map(dept => dept.approved_by_chancellor),
                                    backgroundColor: '#4BC0C0',
                                    borderColor: '#4BC0C0',
                                    borderWidth: 1
                                  }
                                ]
                              }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                  legend: {
                                    position: 'top',
                                    labels: {
                                      usePointStyle: true,
                                      padding: 10,
                                      font: { size: 10 }
                                    }
                                  },
                                  tooltip: {
                                    callbacks: {
                                      label: function(context) {
                                        const label = context.dataset.label || '';
                                        const value = context.parsed.y;
                                        return `${label}: ${value}`;
                                      }
                                    }
                                  }
                                },
                                scales: {
                                  y: {
                                    beginAtZero: true,
                                    ticks: {
                                      stepSize: 1,
                                      font: { size: 10 }
                                    }
                                  },
                                  x: {
                                    ticks: {
                                      maxRotation: 45,
                                      minRotation: 45,
                                      font: { size: 9 }
                                    }
                                  }
                                }
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="text-center text-muted py-3">
                          No department data available
                        </div>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

        </>
      ) : isElevatedRole && loading ? (
        /* Loading state for elevated roles */
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading dashboard data...</span>
          </div>
          <p className="mt-3 text-muted">Loading enhanced dashboard...</p>
        </div>
      ) : isElevatedRole ? (
        /* Error state for elevated roles */
        <div className="text-center py-5">
          <div className="alert alert-warning" role="alert">
            <h5>Unable to load dashboard data</h5>
            <p>Please try refreshing the page or contact support if the issue persists.</p>
          </div>
        </div>
      ) : isExtensionCoordinator ? (
        /* Extension Coordinator Dashboard */
        <div>
          <Row className="g-3 mb-4">
            <Col md={3}>
              <Card className="h-100 shadow-sm border-0">
                <Card.Body className="d-flex align-items-center justify-content-between">
                  <div>
                    <div className="text-muted small">On-Going Projects</div>
                    <div className="fs-2 fw-semibold">{stats.projectsByStatus?.['On-Going'] || 0}</div>
                  </div>
                  <i className="bi bi-play-circle-fill fs-1 text-success"></i>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="h-100 shadow-sm border-0">
                <Card.Body className="d-flex align-items-center justify-content-between">
                  <div>
                    <div className="text-muted small">Pending Approvals</div>
                    <div className="fs-2 fw-semibold">{stats.projectsByStatus?.Pending || 0}</div>
                  </div>
                  <i className="bi bi-hourglass-split fs-1 text-warning"></i>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="h-100 shadow-sm border-0">
                <Card.Body className="d-flex align-items-center justify-content-between">
                  <div>
                    <div className="text-muted small">Approved Events</div>
                    <div className="fs-2 fw-semibold">{stats.projectsByStatus?.['Approved by Chancellor'] || 0}</div>
                  </div>
                  <i className="bi bi-check-circle-fill fs-1 text-success"></i>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="h-100 shadow-sm border-0">
                <Card.Body className="d-flex align-items-center justify-content-between">
                  <div>
                    <div className="text-muted small">Total Events</div>
                    <div className="fs-2 fw-semibold">{(stats.totalPrograms || 0) + (stats.totalProjects || 0) + (stats.totalActivities || 0)}</div>
                  </div>
                  <i className="bi bi-folder-fill fs-1 text-primary"></i>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Recent Activity List */}
          <Row className="mb-4">
            <Col>
              <Card className="shadow-sm">
                <Card.Header>
                  <h5 className="mb-0">Recent Activity</h5>
                </Card.Header>
                <Card.Body>
                  <div className="recent-activity-list">
                    {stats.recentActivity && stats.recentActivity.length > 0 ? (
                      stats.recentActivity.slice(0, 5).map((activity, index) => (
                        <div key={index} className="d-flex align-items-center mb-3 pb-3 border-bottom">
                          <div className="me-3">
                            <i className={`bi ${getActivityIcon(activity.action)} fs-4 text-${getActivityColor(activity.action)}`}></i>
                          </div>
                          <div className="flex-grow-1">
                            <div className="fw-medium">
                              {getActivityDescription(activity)}
                            </div>
                            <div className="text-muted small">
                              {formatDate(activity.created_at)}
                              {activity.entity_id && ` • #${activity.entity_id}`}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-muted py-4">
                        <i className="bi bi-clock-history fs-1 mb-3"></i>
                        <p>No recent activity found</p>
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      ) : (
        /* Regular Dashboard for Other Non-Elevated Roles */
        <div>
          <Row className="g-3 mb-4">
            <Col md={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="d-flex align-items-center justify-content-between">
              <div>
                <div className="text-muted small">Total Programs</div>
                <div className="fs-2 fw-semibold">{stats.totalPrograms}</div>
              </div>
              <i className="bi bi-diagram-3-fill fs-1 text-primary"></i>
            </Card.Body>
          </Card>
            </Col>
            <Col md={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="d-flex align-items-center justify-content-between">
              <div>
                <div className="text-muted small">Total Projects</div>
                <div className="fs-2 fw-semibold">{stats.initiativesByType?.Project || stats.totalProjects || 0}</div>
              </div>
              <i className="bi bi-folder-fill fs-1 text-danger"></i>
            </Card.Body>
          </Card>
            </Col>
            <Col md={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="d-flex align-items-center justify-content-between">
              <div>
                <div className="text-muted small">Total Activities</div>
                <div className="fs-2 fw-semibold">{stats.totalActivities}</div>
              </div>
              <i className="bi bi-star-fill fs-1 text-warning"></i>
            </Card.Body>
          </Card>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
